import { i18n } from "../translate/i18n";

/**
 * WhatsApp Username/BSUID (Meta 2026) — contact-identifier formatter.
 *
 * A phone-less username adopter is stored with a synthetic "wau_<bsuid>" value in
 * `contact.number` (so NOT NULL + UNIQUE(number, companyId) still hold). That raw
 * key must NEVER be shown to a user or made callable. This helper is the single
 * source of truth for rendering a contact's identifier across the app: it returns
 * the "@username" (or a friendly label) for a BSUID contact, and the plain phone
 * number for a normal phone contact.
 *
 * Mirrors the fb_/ig_/tg_ prefix family already used for omnichannel contacts.
 */

export const BSUID_PREFIX = "wau_";

export const isBsuidNumber = number =>
  typeof number === "string" && number.startsWith(BSUID_PREFIX);

/**
 * @param {object} contact contact-like object ({ number, whatsappUsername, identityType })
 * @returns {{ display: string, isSynthetic: boolean, callable: boolean, username: string }}
 *   display     — safe text to show (never the raw wau_ key)
 *   isSynthetic — true for a BSUID-only contact (no phone)
 *   callable    — true only for a real phone number (drives tel:/call buttons)
 *   username    — the @handle without the leading '@' (may be empty)
 */
export const formatContactIdentifier = contact => {
  const number = (contact && contact.number) || "";
  const username = (contact && contact.whatsappUsername) || "";
  const synthetic =
    isBsuidNumber(number) || (contact && contact.identityType === "bsuid");

  if (synthetic) {
    const label = i18n.t("contacts.whatsappUser");
    const display = username
      ? `@${username}`
      : label && label !== "contacts.whatsappUser"
      ? label
      : "WhatsApp user";
    return { display, isSynthetic: true, callable: false, username };
  }

  return { display: number, isSynthetic: false, callable: !!number, username };
};

export default formatContactIdentifier;
